import React, { useEffect } from 'react';
import { useSearchFlightMutation } from '@flysync/shared-redux/services/booking';
import { useSelector, useDispatch } from 'react-redux';
import {
  setSelectBucket,
  setReturnBucket,
  updateField,
  isLoader as setIsLoading,
} from '@flysync/shared-redux/slices/flightSearchSlice';
import { resetFilter } from '@flysync/shared-redux/slices/filterSlice';
import { Root } from '@/components/list/flightlist/types';
import {
  setSelectedFlight,
  setSelectedReturnFlight,
} from '@flysync/shared-redux/slices/userInputSlice';
import { config } from '@/config';
import {
  setOnwardFlight,
  setReturnFlight,
  setFilteredOnwardData,
  setFilteredReturnData,
} from '@flysync/shared-redux/slices/flightDetails';
import { format } from 'date-fns';

export const useFlightSearchApi = () => {
  const [searchFlight, {}] = useSearchFlightMutation({
    fixedCacheKey: 'flight-search',
  });
  const [searchResults, setSearchResults] = React.useState<any>({
    outBoundFlight: null,
    returnFlight: null,
  });
  const [isLoading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const dispatch = useDispatch();

  const flightSearchInput = useSelector((state: any) => state.flightSearch);

  const providerCodes = config.providerCodes || [];

  /**
   * Deep-merge filter objects from multiple providers.
   * - stops: OR-merge booleans (e.g. { zero: true } + { one: true } => { zero: true, one: true })
   * - departure_time: OR-merge booleans
   * - priceRange: take global min/max across providers
   * - airline: concatenate and deduplicate by airline name
   * - other keys: merge with spread (last wins for unknown keys)
   */
  const mergeFilters = (existing: any, incoming: any): any => {
    if (!existing || Object.keys(existing).length === 0) return { ...incoming };
    if (!incoming || Object.keys(incoming).length === 0) return { ...existing };

    const merged: any = { ...existing };

    for (const key of Object.keys(incoming)) {
      if (key === 'stops' || key === 'departure_time') {
        // OR-merge boolean maps: { zero: true, one: false } + { zero: true, two: true }
        // => { zero: true, one: false, two: true }
        merged[key] = { ...(existing[key] || {}) };
        const incomingObj = incoming[key] || {};
        for (const subKey of Object.keys(incomingObj)) {
          // OR logic: if either provider has true, result is true
          merged[key][subKey] = merged[key][subKey] || incomingObj[subKey];
        }
      } else if (key === 'priceRange') {
        // Take global min of mins and max of maxes
        const existingRange = existing[key] || {};
        const incomingRange = incoming[key] || {};
        merged[key] = {
          min: Math.min(
            existingRange.min ?? Infinity,
            incomingRange.min ?? Infinity,
          ),
          max: Math.max(
            existingRange.max ?? 0,
            incomingRange.max ?? 0,
          ),
        };
        // Handle edge case where min was Infinity (no valid values)
        if (merged[key].min === Infinity) merged[key].min = 0;
      } else if (key === 'airline') {
        // Concatenate airline arrays and deduplicate by airline name
        const existingAirlines = existing[key] || [];
        const incomingAirlines = incoming[key] || [];
        const airlineMap = new Map<string, any>();
        for (const a of [...existingAirlines, ...incomingAirlines]) {
          if (a?.airline && !airlineMap.has(a.airline.toLowerCase())) {
            airlineMap.set(a.airline.toLowerCase(), a);
          }
        }
        merged[key] = Array.from(airlineMap.values());
      } else {
        // For any other unknown filter keys, just use the incoming value
        merged[key] = incoming[key];
      }
    }

    return merged;
  };

  const performFlightSearch = async (travelType: string) => {
    try {
      dispatch(resetFilter());

      dispatch(setOnwardFlight([]));
      dispatch(setReturnFlight([]));
      dispatch(setFilteredOnwardData([]));
      dispatch(setFilteredReturnData([]));

      sessionStorage.removeItem('onwardFilter');
      sessionStorage.removeItem('returnFilter');
      setLoading(true);
      dispatch(setIsLoading(true));

      const reqData = {
        origin: flightSearchInput?.origin,
        destination: flightSearchInput?.destination,
        date: format(flightSearchInput?.date, 'yyyy-MM-dd'),
        directFlight: 'N',
        airlineName: '',
        fareType: flightSearchInput?.fareType,
        tripType:
          flightSearchInput?.originCountry !=
            flightSearchInput?.destinationCountry &&
          flightSearchInput?.travelType === 'RT'
            ? 'INRT'
            : flightSearchInput?.tripType,
        travelMode:
          flightSearchInput?.originCountry ==
          flightSearchInput?.destinationCountry
            ? 'DO'
            : 'IN',
        travelType: flightSearchInput?.travelType,
        originCountry: flightSearchInput?.originCountry,
        destinationCountry: flightSearchInput?.destinationCountry,
        rt_date:
          flightSearchInput?.originCountry !=
            flightSearchInput?.destinationCountry &&
          flightSearchInput?.travelType === 'RT'
            ? format(flightSearchInput?.rt_date, 'yyyy-MM-dd')
            : '',
        passengers: {
          adult: flightSearchInput?.passengers?.adult,
          child: flightSearchInput?.passengers?.child,
          infant: flightSearchInput?.passengers?.infant,
          cabin: flightSearchInput?.passengers?.cabin,
          cabinId: flightSearchInput?.passengers?.cabinId,
        },
        isRT: false,
        providerCode: flightSearchInput?.providerCode,
        product_type: 'B2C',
      };

      const originRecentSearch = sessionStorage.getItem('originRecentSearch')
        ? JSON.parse(sessionStorage.getItem('originRecentSearch') || '[]')
        : [];
      const destinationRecentSearch = sessionStorage.getItem(
        'destinationRecentSearch',
      )
        ? JSON.parse(sessionStorage.getItem('destinationRecentSearch') || '[]')
        : [];

      let getOriginRecentSearch = originRecentSearch.filter(
        (item: any) => item.airport_code !== flightSearchInput.origin,
      );

      if(config.corporateName != 'Trailfinder'){
        getOriginRecentSearch.unshift({
          city_name: flightSearchInput.origin_city,
          airport_name: flightSearchInput.origin_airport,
          airport_code: flightSearchInput.origin,
          country_name: flightSearchInput.originCountry,
      });
    }

      let getDestinationRecentSearch = destinationRecentSearch.filter(
        (item: any) => item.airport_code !== flightSearchInput.destination,
      );

      if(config.corporateName != 'Trailfinder'){
        getDestinationRecentSearch.unshift({
          city_name: flightSearchInput.destination_city,
          airport_name: flightSearchInput.destination_airport,
          airport_code: flightSearchInput.destination,
          country_name: flightSearchInput.destinationCountry,
      });
    }

      getOriginRecentSearch = getOriginRecentSearch.slice(0, 5);
      getDestinationRecentSearch = getDestinationRecentSearch.slice(0, 5);

      sessionStorage.setItem(
        'originRecentSearch',
        JSON.stringify(getOriginRecentSearch),
      );
      sessionStorage.setItem(
        'destinationRecentSearch',
        JSON.stringify(getDestinationRecentSearch),
      );

      const cityData = {
        origin: flightSearchInput.origin,
        destination: flightSearchInput.destination,
        origin_city: flightSearchInput.origin_city,
        destination_city: flightSearchInput.destination_city,
        originCountry: flightSearchInput.originCountry,
        destinationCountry: flightSearchInput.destinationCountry,
        origin_airport: flightSearchInput.origin_airport,
        destination_airport: flightSearchInput.destination_airport,
      };

      sessionStorage.setItem('cityData', JSON.stringify(cityData));

      const promises = providerCodes.map((providerCode) => {
        return searchFlight({
          input: {
            ...reqData,
            providerCode,
          },
        }).unwrap();
      });

      const results = await Promise.all(promises);

      let outBoundData = results.reduce(
        (acc, curr) => {
          acc.data.push(...curr.data);
          acc.filter = mergeFilters(acc.filter, curr.filter);
          return acc;
        },
        { data: [], filter: {} as any },
      );

      // const bucketFareData = handleBucketFare(outBoundData.data);
      // console.log('bucketFareData', bucketFareData, outBoundData.data);
      outBoundData = {
        ...outBoundData,
        data: handleBucketFare(outBoundData.data),
        filter: {
          ...outBoundData.filter,
          airline: handleFilter(outBoundData.data, outBoundData.filter),
        },
      };

      outBoundData.filter.airline = outBoundData.filter.airline.map(
        (airline: any) => {
          const count = outBoundData.data.filter(
            (flight: any) =>
              flight.airline_code.toLowerCase() ===
                airline.airline.toLowerCase() ||
              flight.airline_name.toLowerCase() ===
                airline.airline.toLowerCase(),
          ).length;
          return {
            ...airline,
            count,
          };
        },
      );

      setSearchResults((prev: any) => ({
        ...prev,
        outBoundFlight: outBoundData,
      }));

      if (outBoundData.data.length > 0) {
        // sessionStorage.setItem('onward', JSON.stringify(outBoundData.data));
        dispatch(setOnwardFlight(outBoundData.data));
        dispatch(
          setFilteredOnwardData(JSON.parse(JSON.stringify(outBoundData.data))),
        );
        dispatch(setSelectedFlight(outBoundData.data[0]));
        dispatch(setSelectBucket(outBoundData.data[0].price_details));
      }

      if (
        flightSearchInput?.travelType === 'RT' &&
        flightSearchInput?.travelMode === 'DO'
      ) {
        const tempDestination = flightSearchInput?.origin;
        const tempDestinationCountry = flightSearchInput?.originCountry;
        const data = {
          ...flightSearchInput,
          product_type: reqData.product_type,
          destination: tempDestination,
          destinationCountry: tempDestinationCountry,
          origin: flightSearchInput?.destination,
          originCountry: flightSearchInput?.destinationCountry,
          isRT: true,
          date: flightSearchInput?.rt_date,
          tripType: 'OW',
          travelType: 'RT',
        };

        const promises = providerCodes.map((providerCode) => {
          return searchFlight({
            input: { ...data, providerCode },
          }).unwrap();
        });

        const results = await Promise.all(promises);

        let returnData = results.reduce(
          (acc, curr) => {
            acc.data.push(...curr.data);
            acc.filter = mergeFilters(acc.filter, curr.filter);
            return acc;
          },
          { data: [], filter: {} as any },
        );

        returnData = {
          ...returnData,
          data: handleBucketFare(returnData.data),
          filter: {
            ...returnData.filter,
            airline: handleFilter(returnData.data, returnData.filter),
          },
        };

        setSearchResults((prev: any) => ({
          ...prev,
          returnFlight: returnData,
        }));
        if (returnData.data.length > 0) {
          // sessionStorage.setItem('return', JSON.stringify(returnData.data));
          dispatch(setReturnFlight(returnData.data));

          dispatch(setFilteredReturnData(returnData.data));
          dispatch(setSelectedReturnFlight(returnData.data[0]));
          dispatch(setReturnBucket(returnData.data[0].price_details));
        }
      }
    } catch (e) {
      setError(e as any);
    } finally {
      setLoading(false);
      dispatch(setIsLoading(false));
    }
  };

  const handleFilter = (
    data: Root[],
    filter: { airline: Array<{ airline: string }> },
  ) => {
    if (!data || !filter?.airline) return [];

    return filter.airline.map((airline: any) => {
      return {
        ...airline,
        count: data.filter(
          (flight) =>
            flight.airline_code?.toLowerCase() ===
            airline.airline?.toLowerCase(),
        ).length,
      };
    });
  };

  const handleBucketFare = (flights: any) => {
    const flightMap: Record<string, any> = {};

    flights.forEach((flight: any) => {
      const flightNumber = flight?.flight_number
        ?.replace(/\s+/g, '') // Remove extra spaces
        ?.trim();

      if (!flightMap[flightNumber]) {
        flightMap[flightNumber] = {
          ...flight,
          price_details: flight?.price_details
            ? [...flight?.price_details]
            : [], // Ensure price_details is not missing
        };
      } else {
        const existingFlight = flightMap[flightNumber];
        existingFlight.price_details = [
          ...existingFlight.price_details,
          ...(flight?.price_details || []), // Ensure price_details is not missing
        ];
      }
    });

    return Object.values(flightMap);
  };

  return { isLoading, error, searchResults, performFlightSearch, setLoading };
};
